import { Button, Popover, PopoverContent, PopoverTrigger, Switch, Tab, Tabs } from '@heroui/react';
import { Replace, Upload } from 'lucide-react';
import React, { FC } from 'react';

import { useGetAdAssets } from '../../../../queries';
import { useAssetUpload, useSceneEditing } from '../../hooks';
import { ImageElement } from '../../model/types';
import { PanelSection, SliderField } from '../common';
import { AssetGrid, AssetThumb, AssetTile, EmptyPanelHint } from '../Editor/Editor.styled';

interface ImagePropertiesProps {
  element: ImageElement;
}

export const ImageProperties: FC<ImagePropertiesProps> = ({ element }) => {
  const { patchElement } = useSceneEditing();
  const { data: assets = [] } = useGetAdAssets();
  const { inputRef, accept, isUploading, openPicker, handleFiles } = useAssetUpload();

  return (
    <PanelSection title="Zdjęcie">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={async (event) => {
          const uploaded = await handleFiles(event.target.files);
          if (uploaded[0]) patchElement(element.id, { src: uploaded[0].url });
        }}
      />

      <Popover placement="left" showArrow>
        <PopoverTrigger>
          <Button size="sm" radius="sm" variant="flat" startContent={<Replace size={16} />}>
            {element.src ? 'Podmień zdjęcie' : 'Wybierz zdjęcie'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="max-h-80 w-64 overflow-y-auto p-3">
          <Button
            size="sm"
            radius="sm"
            variant="flat"
            className="mb-2 w-full"
            startContent={<Upload size={16} />}
            isLoading={isUploading}
            onPress={openPicker}
          >
            Wgraj nowe
          </Button>

          {assets.length === 0 ? (
            <EmptyPanelHint>Biblioteka jest pusta.</EmptyPanelHint>
          ) : (
            <AssetGrid>
              {assets.map((asset) => (
                <AssetTile key={asset.id}>
                  <AssetThumb
                    type="button"
                    title={asset.name}
                    onClick={() => patchElement(element.id, { src: asset.url })}
                  >
                    <img src={asset.url} alt="" crossOrigin="anonymous" />
                  </AssetThumb>
                </AssetTile>
              ))}
            </AssetGrid>
          )}
        </PopoverContent>
      </Popover>

      <Tabs
        size="sm"
        radius="sm"
        fullWidth
        aria-label="Dopasowanie zdjęcia"
        selectedKey={element.fit}
        onSelectionChange={(key) => patchElement(element.id, { fit: key as ImageElement['fit'] })}
      >
        <Tab key="cover" title="Wypełnij" />
        <Tab key="contain" title="Zmieść" />
      </Tabs>
      <Switch
        size="sm"
        isSelected={element.circle}
        onValueChange={(circle) => patchElement(element.id, { circle })}
      >
        <span className="text-xs">Maska koła</span>
      </Switch>
      {element.fit === 'cover' && (
        <>
          <SliderField
            label="Kadr w poziomie"
            suffix="%"
            min={0}
            max={100}
            value={Math.round((element.focusX ?? 0.5) * 100)}
            onChange={(value) => patchElement(element.id, { focusX: value / 100 }, `focus-x-${element.id}`)}
          />
          <SliderField
            label="Kadr w pionie"
            suffix="%"
            min={0}
            max={100}
            value={Math.round((element.focusY ?? 0.5) * 100)}
            onChange={(value) => patchElement(element.id, { focusY: value / 100 }, `focus-y-${element.id}`)}
          />
        </>
      )}

      <SliderField
        label="Rozmycie"
        suffix=" px"
        min={0}
        max={40}
        value={element.blur ?? 0}
        onChange={(blur) => patchElement(element.id, { blur }, `blur-${element.id}`)}
      />
      <SliderField
        label="Jasność"
        min={-100}
        max={100}
        value={Math.round((element.brightness ?? 0) * 100)}
        onChange={(value) => patchElement(element.id, { brightness: value / 100 }, `brightness-${element.id}`)}
      />
      <SliderField
        label="Kontrast"
        min={-100}
        max={100}
        value={Math.round((element.contrast ?? 0) * 100)}
        onChange={(value) => patchElement(element.id, { contrast: value / 100 }, `contrast-${element.id}`)}
      />
      <Switch
        size="sm"
        isSelected={Boolean(element.grayscale)}
        onValueChange={(grayscale) => patchElement(element.id, { grayscale })}
      >
        <span className="text-xs">Czarno-białe</span>
      </Switch>

      {!element.circle && (
        <SliderField
          label="Zaokrąglenie rogów"
          suffix=" px"
          min={0}
          max={Math.round(Math.min(element.width, element.height) / 2)}
          value={element.radius}
          onChange={(radius) => patchElement(element.id, { radius }, `image-radius-${element.id}`)}
        />
      )}
    </PanelSection>
  );
};
